/**
 * Which trimester it is, derived from the DATE.
 *
 * ⚠️ It used to be read from `ref_trimester_m1` on the TA Cockpit sheet, which
 * is maintained by hand. On 10 Sep 2026 that cell still said "May 2026", so the
 * page was captioned "T2 · May – August 2026" and — far worse — the hiring
 * demand board ran as a T2 round. "Hired this trimester" therefore counted every
 * offer accepted since MAY: 77 instead of 9.
 *
 * A trimester is four calendar months. That is a fact about the calendar, not a
 * setting, so nothing here can go stale.
 */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export interface Trimester {
  /** `t3-2026` — the key the demand board is scoped by. */
  key: string;
  number: 1 | 2 | 3;
  year: number;
  /** `2026-09`, the first month. What "this trimester" counts from. */
  startMonth: string;
  /** "T3 · September – December 2026" */
  label: string;
}

/**
 * The three trimester-END months (04/08/12) as two-digit strings — the calendar fact this whole
 * file exists to keep in one place. Used by anything that needs to know "is this month the end of
 * a trimester" without re-deriving `Math.ceil(month / 4)` itself (e.g. a monthly timeline that
 * collapses to trimester-end snapshots).
 */
export const TRIMESTER_END_MONTH_NUMBERS: ReadonlySet<string> = new Set(['04', '08', '12']);

/** `'04' → 'T1'`, `'08' → 'T2'`, `'12' → 'T3'` — for labeling a trimester-end column. */
export const TRIMESTER_NUMBER_BY_END_MONTH: Readonly<Record<string, string>> = {
  '04': 'T1',
  '08': 'T2',
  '12': 'T3',
};

export function currentTrimester(today: Date = new Date()): Trimester {
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // 1-12
  const number = Math.ceil(month / 4) as 1 | 2 | 3;
  const first = (number - 1) * 4 + 1;
  return {
    key: `t${number}-${year}`,
    number,
    year,
    startMonth: `${year}-${String(first).padStart(2, '0')}`,
    label: `T${number} · ${MONTHS[first - 1]} – ${MONTHS[first + 2]} ${year}`,
  };
}
